import React, { useCallback, useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getPayrollItemTableConfig } from "@/config/tableConfigs/payrollItemTableConfig";
import { payrollItemService } from "@/services/payrollItemService";
import { PayrollItemType } from "@/types/db/payroll_items";
import useAuth from "@/hooks/useAuth";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import useToast from "@/hooks/useToast";
import { FromDB } from "@/types";
import useContextMenu from "@/hooks/useContextMenu";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { StyleSheet, View } from "react-native";
import { router } from "expo-router";
import { DeletePayrollItemModal } from "@/components/Modals/DeletePayrollItemModal";
import { ExportPayrollItemModal } from "@/components/Modals/ExportPayrollItemModal";
import { useModal } from "@/hooks/useModal";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { convertToCSV, downloadCSV } from "@/lib/csvUtils";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function PayrollPage() {
  const { colors } = useTheme();
  const { userRoles, displayNames } = useAuth();
  const toast = useToast();
  const contextMenu = useContextMenu();
  const { openModal, closeModal } = useModal();
  const { triggerRefresh } = useRowSelectionContext();
  useSetPageTitle(displayNames?.payroll?.plural);

  // Extract numeric rate from rate string like "$14.00/hr" or "$21.00/hr (OT)"
  const extractRate = (rateStr: string | undefined): string => {
    if (!rateStr) return "0.00";
    const match = rateStr.match(/\$?([\d.]+)/);
    return match ? parseFloat(match[1]).toFixed(2) : "0.00";
  };

  // Format date as YYYY-MM-DD
  const formatDate = (isoString: string | null): string => {
    if (!isoString) return "";
    return isoString.split("T")[0];
  };

  // Format time as HH:MM
  const formatTime = (isoString: string | null): string => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toTimeString().slice(0, 5);
  };

  // Format date as MM-DD-YYYY for note
  const formatNoteDate = (isoString: string | null): string => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const y = date.getFullYear();
    return `${m}-${d}-${y}`;
  };

  // Format date as M-DD-YY for non-worked note
  const formatShortDate = (isoString: string | null): string => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const m = date.getMonth() + 1;
    const d = String(date.getDate()).padStart(2, "0");
    const y = String(date.getFullYear()).slice(-2);
    return `${m}-${d}-${y}`;
  };

  const runEvereeExport = useCallback(
    async ({ selectedIds }: any) => {
      const freshData = await payrollItemService.getPayrollItems();
      const selectedSet = new Set(selectedIds);
      const selected = freshData.filter((item: any) => selectedSet.has(item.id));

      // Split by unit type
      const workedItems = selected.filter((item: any) => item.unit?.unit === "worked");
      const nonWorkedItems = selected.filter((item: any) => item.unit?.unit !== "worked");

      // Generate Worked Time CSV
      if (workedItems.length > 0) {
        const workedData = workedItems.map((item: any) => ({
          "External Worker ID": item.everee_id || "",
          "Worker Name": item.worker?.name || "",
          "Start Day": formatDate(item.start_time),
          "Start Time": formatTime(item.start_time),
          "End Day": formatDate(item.end_time),
          "End Time": formatTime(item.end_time),
          Verified: "TRUE",
          "Override Rate": extractRate(item.rate?.rate),
          Note: `${item.shift?.name || ""} on ${formatNoteDate(item.start_time)}`,
        }));

        const columns = [
          "External Worker ID",
          "Worker Name",
          "Start Day",
          "Start Time",
          "End Day",
          "End Time",
          "Verified",
          "Override Rate",
          "Note",
        ];
        const csv = convertToCSV(workedData, columns);
        downloadCSV(csv, "everee worked time.csv");
      }

      // Generate Non-Worked Time CSV
      if (nonWorkedItems.length > 0) {
        const nonWorkedData = nonWorkedItems.map((item: any) => ({
          "External Worker ID": item.everee_id || "",
          "Worker Name": item.worker?.name || "",
          Amount: item.amount?.amount?.toFixed(2) || "0.00",
          Note: `${item.shift?.name || ""} GH ${formatShortDate(item.start_time)}`,
        }));

        const columns = ["External Worker ID", "Worker Name", "Amount", "Note"];
        const csv = convertToCSV(nonWorkedData, columns);
        downloadCSV(csv, "everee non worked time.csv");
      }

      // Update status to EXPORTED
      try {
        await payrollItemService.updatePayrollItemStatus(selectedIds, "EXPORTED");
      } catch (err: any) {
        toast.showToast({ title: "Failed to update status", message: err.message, toastType: "error" });
      }

      // Show toast with results
      const parts: string[] = [];
      if (workedItems.length > 0) parts.push(`${workedItems.length} worked`);
      if (nonWorkedItems.length > 0) parts.push(`${nonWorkedItems.length} non-worked`);

      if (parts.length > 0) {
        toast.showToast({
          title: `Exported ${parts.join(" and ")} items`,
          toastType: "success",
        });
      }

      // Trigger table refresh to reflect status change
      triggerRefresh("payroll item");
    },
    [toast, triggerRefresh],
  );

  const handleEvereeExport = useCallback(
    async ({ selectedIds, selectedMap }: any) => {
      const skipConfirmation = await AsyncStorage.getItem("payroll_export_skip_confirmation");

      if (skipConfirmation === "true") {
        await runEvereeExport({ selectedIds, selectedMap });
        return;
      }

      openModal(
        <ExportPayrollItemModal
          count={selectedIds.length}
          onConfirm={async (dontShowAgain) => {
            closeModal();
            if (dontShowAgain) {
              await AsyncStorage.setItem("payroll_export_skip_confirmation", "true");
            }
            await runEvereeExport({ selectedIds, selectedMap });
          }}
          onCancel={closeModal}
        />,
      );
    },
    [openModal, closeModal, runEvereeExport],
  );

  const baseConfig = useMemo(() => getPayrollItemTableConfig(userRoles, displayNames), [userRoles, displayNames]);

  const tableConfig = useMemo(() => baseConfig, [baseConfig]);

  const hasPayrollAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.payroll?.read === true) ?? false;
  }, [userRoles]);

  const handleDuplicate = async (row: any) => {
    try {
      await payrollItemService.duplicatePayrollItem(row.id);
      toast.showToast({ title: "Payroll item duplicated", toastType: "success" });
    } catch (error: any) {
      toast.showToast({ title: "Failed to duplicate", message: error.message, toastType: "error" });
    }
  };

  const menuContent = (row: any) => (
    <View>
      <Button
        text="Duplicate"
        iconSize={18}
        icon="content-duplicate"
        style={styles.menuItems}
        onPress={() => {
          contextMenu.setVisible(false);
          handleDuplicate(row);
        }}
      />
      <Button
        text="View"
        iconSize={18}
        icon="exit-to-app"
        style={styles.menuItems}
        onPress={() => {
          router.setParams({ panel: "payroll", id: row.id });
          router.setParams({ new: undefined });
          contextMenu.setVisible(false);
        }}
      />
    </View>
  );

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<PayrollItemType>> = useMemo(
    () => ({
      storageKey: "payroll_item_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeletePayrollItemModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("payroll_item_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasPayrollAccess) {
    return <NoPermissionScreen entityName={displayNames?.payroll?.plural || "Payroll"} />;
  }

  return (
    <EntityTablePage
      {...tableConfig}
      fetchData={payrollItemService.getPayrollItems}
      updateEntities={payrollItemService.saveRowPatches}
      deleteEntities={payrollItemService.deletePayrollItemsByIds}
      deleteConfirmation={deleteConfirmation as any}
      enableSave
      enableCreate
      newRequiresSelect
      rowNavIcon="dots-vertical"
      selectionActions={({ selectedIds, selectedMap }) => [
        {
          id: "export-everee",
          label: "Export to Everee",
          icon: "export-variant",
          onPress: () => handleEvereeExport({ selectedIds, selectedMap }),
        },
      ]}
      onAction={(row, ref) => {
        if (!ref) return;
        ref.current?.measureInWindow((x, y, _w, h) => {
          contextMenu.setLocation({ x, y: y + (h ?? 0) });
        });
        contextMenu.setMenuContent(menuContent(row));
        contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
        contextMenu.setVisible(true);
      }}
    />
  );
}

const styles = StyleSheet.create({
  menuItems: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
